import { msg } from "@lingui/core/macro";
import { type Component, type JSX, Show } from "solid-js";
import { useI18n } from "@/providers/i18n-provider";
import { GlassSurface } from "./glass-surface";
import { Button } from "./button";

interface NotFoundStateProps {
  /** Emoji/illustration shown above the title. */
  emoji?: string;
  /** Title and description (override the playful defaults). */
  title?: string;
  description?: string;
  /** Primary action (e.g. go back home). */
  primaryAction?: { label: string; onClick: () => void };
  /** Optional secondary action. */
  secondaryAction?: { label: string; onClick: () => void };
  class?: string;
}

/**
 * "Not found" state for routes whose entity (conversation, URL) could not be
 * resolved. Reuses the repo's premium `GlassSurface` and `Button` with localized
 * copy, instead of leaving a broken or blank screen. Susurro's serene
 * aesthetics: a clear symbol and a single CTA.
 */
export const NotFoundState: Component<NotFoundStateProps> = (props) => {
  const { t } = useI18n();

  const title = (): string => props.title ?? t(msg`We can't find that`);
  const description = (): string =>
    props.description ?? t(msg`The page you're looking for isn't here. Let's go back home!`);

  const actions = (): JSX.Element => (
    <div class="flex w-full flex-col items-center gap-3 sm:flex-row sm:justify-center">
      <Show when={props.primaryAction}>
        {(primary) => (
          <Button
            size="lg"
            class="w-full text-base sm:w-auto"
            onClick={() => {
              primary().onClick();
            }}
          >
            {primary().label}
          </Button>
        )}
      </Show>
      <Show when={props.secondaryAction}>
        {(secondary) => (
          <Button
            variant="outline"
            size="lg"
            class="w-full text-base sm:w-auto"
            onClick={() => {
              secondary().onClick();
            }}
          >
            {secondary().label}
          </Button>
        )}
      </Show>
    </div>
  );

  return (
    <GlassSurface
      class={`flex w-full max-w-md flex-col items-center gap-5 p-8 text-center ${props.class ?? ""}`}
    >
      <span class="animate-scale-in text-6xl sm:text-7xl" aria-hidden="true">
        {props.emoji ?? "🔒"}
      </span>
      <div class="space-y-2">
        <h2 class="text-2xl font-bold tracking-tight text-foreground">{title()}</h2>
        <p class="text-sm text-muted-foreground">{description()}</p>
      </div>
      <Show when={props.primaryAction ?? props.secondaryAction}>{actions()}</Show>
    </GlassSurface>
  );
};
